import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import {
	COVER_PICTURE_REMOVE_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD as DEFAULT_UNSPLASH_QUERY_WORD } from '@confluence/custom-sites-extensions';
import { UnsplashDropdownMenu } from '@confluence/content-topper';
import { fg } from '@confluence/feature-gating';

import type { HoverOverlayAndDropDownItemsComponentProps } from '../pageCoverPictureTypes';

import { getUnsplashPhotos } from './unsplashAPI';

type HoverOverlayProps = {
	isDropdownOpen: boolean;
	ref?: any;
	userOffline?: boolean;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
};

const COVER_IMAGE_FIXED_HEIGHT = 147;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HoverOverlay = styled.div<HoverOverlayProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		props.isFixedWidthImageOption && fg('confluence_frontend_cover_image_ssr')
			? `${COVER_IMAGE_FIXED_HEIGHT}px`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				!props.isFixedWidthImageOption && fg('confluence_frontend_cover_image_ssr')
				? 'round(down, calc(100cqw / (38 / 7)), 1px)'
				: `${props.dynamicCoverImageHeight}px`,
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.background.neutral.hovered'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.userOffline ? 'not-allowed' : 'pointer'),
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.isDropdownOpen ? 1 : 0),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props) => (props.userOffline ? '0' : '1'),
	},
	'&:focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props) => (props.userOffline ? '0' : '1'),
	},
});

export const HoverOverlayAndDropdownItems = ({
	userOffline,
	closeModal,
	openMediaBrowser,
	setIsRepositionActive,
	onRemoveImage,
	onImageWidthChange,
	handleUnsplashUpload,
	setIsUploadingPicture,
	initialUnsplashInfoArray,
	setInitialUnsplashInfoArray,
	coverPictureState,
	contentId,
	dynamicCoverImageHeight,
	isFixedWidthImageOption,
}: HoverOverlayAndDropDownItemsComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const overlayComponent = useRef<HTMLElement | null>(null);
	const dropdownComponent = useRef<HTMLElement | null>(null);
	const dropdownPopupComponentRef = useRef<HTMLElement | null>(null);

	const isLivePage = useIsCurrentPageLive();

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (
				overlayComponent?.current &&
				event.target instanceof Node &&
				!overlayComponent?.current?.contains(event.target) &&
				!dropdownComponent?.current?.contains(event.target) &&
				!dropdownPopupComponentRef?.current?.contains(event.target) &&
				// only invoke a change if the user is changing the picture.
				// If we keep updating the state to the same value in an unstated container, it will invoke re-renders of all title components.
				coverPictureState.state.isChangingPicture
			) {
				// close parent dropdown if we click anywhere outside
				void coverPictureState.setIsChangingPicture(false);
				setIsDropdownOpen(false);
			}
		},
		[coverPictureState],
	);

	// took inspiration from here:
	// https://bitbucket.org/atlassian/ptc-directory-ui/src/84f964d666578de7d67d99727c734e1231b8588c/packages/common/src/components/ProfileHeader/ProfileHeader.tsx?at=master#lines-83
	// basically if we click outside of the hover overlay, close the dropdown
	useEffect(() => {
		addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	const toggleDropdownOpen = useCallback(
		(event: any) => {
			if (
				event.target instanceof Node &&
				(dropdownComponent?.current?.contains(event.target) ||
					dropdownPopupComponentRef?.current?.contains(event.target))
			) {
				// don't toggle close the dropdown if clicking in the dropdown
				// we need this since the popup is nested
				return;
			}
			if (initialUnsplashInfoArray.length === 0) {
				void getUnsplashPhotos(DEFAULT_UNSPLASH_QUERY_WORD, 1, true).then((results) => {
					setInitialUnsplashInfoArray(results);
				});
			}
			const isChangingPicture = coverPictureState.state.isChangingPicture;
			void coverPictureState.setIsChangingPicture(!isChangingPicture);
			setIsDropdownOpen((prevIsDropdownOpen) => !prevIsDropdownOpen);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'update',
					actionSubjectId: 'updateHeaderImageButtonClicked',
					source: 'editorPageCoverPicture',
					attributes: {
						isLivePage,
					},
				},
			}).fire();
		},
		[
			initialUnsplashInfoArray,
			setInitialUnsplashInfoArray,
			createAnalyticsEvent,
			isLivePage,
			coverPictureState,
		],
	);

	const onRepositionClicked = () => {
		setIsRepositionActive(true);
		void coverPictureState.setIsChangingPicture(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'reposition',
				actionSubjectId: 'repositionHeaderImageButtonClicked',
				source: 'editorPageCoverPicture',
			},
		}).fire();
	};

	const onRemoveImageClicked = () => {
		experienceTracker.start({
			name: COVER_PICTURE_REMOVE_EXPERIENCE,
		});
		onRemoveImage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'remove',
				actionSubjectId: 'removeHeaderImageButtonClicked',
				source: 'editorPageCoverPicture',
				attributes: {
					isLivePage,
				},
			},
		}).fire();
	};

	const onCoverImageWidthDropdownOptionSelected = (isCoverImageWidthChanged: boolean) => {
		if (isCoverImageWidthChanged) {
			setIsDropdownOpen(false);
		}
	};

	const handleFocus = useCallback(() => {
		void coverPictureState.setIsAddHeaderImageButtonFocused(true);
	}, [coverPictureState]);

	const handleBlur = useCallback(() => {
		void coverPictureState.setIsAddHeaderImageButtonFocused(false);
	}, [coverPictureState]);

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div onFocus={handleFocus} onBlur={handleBlur}>
			<HoverOverlay
				data-testid="hover-overlay-custom-sites"
				isDropdownOpen={isDropdownOpen}
				onClick={userOffline ? undefined : toggleDropdownOpen}
				ref={overlayComponent as any}
				userOffline={userOffline}
				dynamicCoverImageHeight={dynamicCoverImageHeight}
				isFixedWidthImageOption={isFixedWidthImageOption}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className="cover-picture-wrapper-dangerously-set-inner-html"
			>
				{!userOffline && (
					<UnsplashDropdownMenu
						dropdownRef={dropdownComponent}
						onDelete={onRemoveImageClicked}
						onReposition={onRepositionClicked}
						imageSrc={coverPictureState.state.coverPictureId}
						isModalOpenState={isDropdownOpen}
						openMediaBrowser={openMediaBrowser}
						initialUnsplashInfoArray={initialUnsplashInfoArray as any}
						closePopup={closeModal}
						userOffline={userOffline}
						handleUnsplashUpload={handleUnsplashUpload}
						setIsUploadingPicture={setIsUploadingPicture}
						coverPictureState={coverPictureState}
						dropdownPopupComponentRef={dropdownPopupComponentRef}
						contentId={contentId}
						onImageWidthChange={onImageWidthChange}
						onCoverImageWidthDropdownOptionSelected={onCoverImageWidthDropdownOptionSelected}
					/>
				)}
			</HoverOverlay>
		</div>
	);
};
